import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { QrCameraReader } from "components/QrCameraReader";
import { Button, ButtonVariant } from "components/Form/Button";
import { Modal } from "components/Modal";
import { ReactComponent as ScanQrCode } from "assets/scan-qr-code.svg";
import {
  MetadataField,
  metadataDisplayValue,
  readMetadataQr,
} from "lib/metadata-qr";

export function MetadataQrPrefill({
  fields,
  values,
  onApply,
  disabled = false,
  collectionId,
  iconOnly = false,
  triggerLabel,
}: {
  fields: MetadataField[];
  values: Record<string, unknown>;
  onApply: (values: Record<string, string>) => void;
  disabled?: boolean;
  collectionId?: string;
  iconOnly?: boolean;
  triggerLabel?: string;
}) {
  const [open, setOpen] = useState(false);
  const [raw, setRaw] = useState("");
  const [manual, setManual] = useState("");
  const [selected, setSelected] = useState(new Set<string>());
  const trigger = useRef<HTMLButtonElement>(null);
  const dialog = useRef<HTMLElement>(null);
  const titleId = useId();
  const enabled = fields.some((field) => field.qrEnabled);
  let review: ReturnType<typeof readMetadataQr> | undefined;
  let error = "";
  if (raw) {
    try {
      review = readMetadataQr(raw, fields, collectionId);
    } catch (problem) {
      error =
        problem instanceof Error ? problem.message : "This QR is invalid.";
    }
  }
  const close = () => {
    setOpen(false);
    trigger.current?.focus();
  };
  useEffect(() => {
    if (!open) return;
    dialog.current?.querySelector<HTMLButtonElement>("button")?.focus();
  }, [open]);
  if (!enabled) return null;
  return (
    <>
      <button
        ref={trigger}
        type="button"
        disabled={disabled}
        aria-label={triggerLabel || "Scan details"}
        className={
          iconOnly
            ? "shrink-0 flex h-[52px] w-[52px] items-center justify-center rounded-xl bg-3461fd text-white disabled:opacity-40"
            : "flex w-full items-center justify-center gap-2 rounded-xl bg-f4f7ff px-4 py-3 text-3461fd font-medium disabled:opacity-40"
        }
        onClick={() => {
          setRaw("");
          setManual("");
          setSelected(
            new Set(
              fields.filter((field) => field.qrEnabled).map((field) => field.id)
            )
          );
          setOpen(true);
        }}
      >
        <ScanQrCode className="h-6 w-6" aria-hidden="true" focusable="false" />
        {!iconOnly && "Scan details"}
      </button>
      {createPortal(
        <Modal show={open} onClose={close}>
          <section
            ref={dialog}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            className="space-y-4"
            onKeyDown={(event) => {
              if (event.key === "Escape") {
                event.stopPropagation();
                close();
              }
              if (event.key === "Tab") {
                const items = Array.from(
                  dialog.current?.querySelectorAll<HTMLElement>(
                    "button:not(:disabled), input, textarea, summary"
                  ) || []
                ).filter((item) => item.getClientRects().length > 0);
                const first = items[0],
                  last = items[items.length - 1];
                if (event.shiftKey && document.activeElement === first) {
                  event.preventDefault();
                  last?.focus();
                } else if (!event.shiftKey && document.activeElement === last) {
                  event.preventDefault();
                  first?.focus();
                }
              }
            }}
          >
            <div className="flex items-center justify-between gap-3">
              <h2 id={titleId} className="text-xl font-semibold">
                {review ? "Review scanned details" : "Scan collection details"}
              </h2>
              <button type="button" onClick={close} className="text-3461fd p-2">
                Close
              </button>
            </div>
            {!raw && (
              <>
                <p className="text-sm text-858494">
                  Scan a station, item or supervisor QR code.
                </p>
                <QrCameraReader onRead={setRaw} />
                <details>
                  <summary className="text-sm text-3461fd py-2">
                    Enter QR contents
                  </summary>
                  <label className="grid gap-2 text-sm">
                    QR contents
                    <textarea
                      value={manual}
                      onChange={(event) => setManual(event.target.value)}
                      maxLength={8192}
                      className="rounded-lg border p-2"
                    />
                  </label>
                  <Button
                    type="button"
                    disabled={!manual.trim()}
                    onClick={() => setRaw(manual.trim())}
                  >
                    Read code
                  </Button>
                </details>
              </>
            )}
            {error && (
              <p role="alert" className="text-fd380f text-sm">
                {error}
              </p>
            )}
            {review && (
              <>
                <p className="text-sm text-858494">
                  Select the details to fill. All values remain editable.
                </p>
                <div>
                  {review.changes.map(({ field, value }) => (
                    <label
                      key={field.id}
                      className="flex items-start gap-3 border-b border-f4f7ff py-3"
                    >
                      <input
                        type="checkbox"
                        className="mt-1"
                        checked={selected.has(field.id)}
                        onChange={(event) =>
                          setSelected((current) => {
                            const next = new Set(current);
                            if (event.target.checked) next.add(field.id);
                            else next.delete(field.id);
                            return next;
                          })
                        }
                      />
                      <span className="min-w-0 break-words">
                        <span className="block text-sm text-858494">
                          {field.title}
                        </span>
                        <span className="block font-medium">
                          {metadataDisplayValue(field, value)}
                        </span>
                        {values[field.id] && values[field.id] !== value ? (
                          <span className="block text-sm text-858494">
                            Replaces{" "}
                            {metadataDisplayValue(field, values[field.id])}
                          </span>
                        ) : null}
                      </span>
                    </label>
                  ))}
                </div>
                {review.ignored > 0 && (
                  <p className="text-sm text-858494">
                    {review.ignored} other details do not apply to this form.
                  </p>
                )}
                <Button
                  type="button"
                  className="w-full"
                  disabled={
                    disabled ||
                    !review.changes.some(({ field }) => selected.has(field.id))
                  }
                  onClick={() => {
                    onApply(
                      Object.fromEntries(
                        review!.changes
                          .filter(({ field }) => selected.has(field.id))
                          .map(({ field, value }) => [field.id, value])
                      )
                    );
                    close();
                  }}
                >
                  Apply selected details
                </Button>
              </>
            )}
            {raw && (
              <Button
                type="button"
                variant={ButtonVariant.FLAT}
                onClick={() => setRaw("")}
              >
                Scan again
              </Button>
            )}
          </section>
        </Modal>,
        document.getElementById("root") || document.body
      )}
    </>
  );
}
